import {
  NodeResult,
  type RunDto,
  RunGroupStatus,
  RunResults,
  RunResultsAggregate,
  RunResultsHistoric,
} from "@custom-types/modelRun"
import { convertLabel } from "@features/graph/utils/graphUtils"
import { RunStatusDto } from "@store/services/runService"

export const getRunName = (run: RunDto | undefined) => run?.run_name || run?.key

export const getRunNamesMap = (runs: RunDto[]) =>
  Object.fromEntries(runs.map((run) => [run.key, getRunName(run)]))

export const readableRunGroupStatus = {
  [RunGroupStatus.DONE]: "Done",
  [RunGroupStatus.RUNNING]: "Running",
  [RunGroupStatus.ERROR]: "Error",
}

export const readableRunStatus = {
  [RunStatusDto.SCHEDULE]: "Running schedule",
  [RunStatusDto.CONVERT]: "Running convert",
  [RunStatusDto.SAMPLE]: "Running sample",
  [RunStatusDto.TRANSFORM]: "Running transform",
  [RunStatusDto.INSERT]: "Running insert",
  [RunStatusDto.DONE]: "Done",
  [RunStatusDto.ERROR]: "Error",
  [RunStatusDto.TIMEOUT]: "Timeout",
}

export const getNodeRunsTimesteps = (nodesRuns: NodeResult[][], maxLag: number = 0) => {
  const timesteps: string[] = []

  nodesRuns.forEach((nodeRun) => {
    // guarantee data is orderly
    const sorted = Object.values(nodeRun).sort((a, b) => (a.timestep > b.timestep ? 1 : -1))
    sorted.forEach((nodeSample, index) => {
      if (index >= maxLag) timesteps.push(String(nodeSample.timestep))
    })
  })

  return Array.from(new Set(timesteps)).sort()
}

export const extractScenarioNameFromRun = (run: RunDto) => {
  const [, ...scenarioWithVersion] = (run.run_name ?? "").split("_")

  return scenarioWithVersion.join("_").split(`(${run.version})`)[0] ?? ""
}

// We want to have narrowed down type that will work both for RunResultsHistoric and RunResultsAggregate.
// These two have similiar structure and the pieces that we need to determine the lag are the same.
type NodeResultsBase = {
  names?: Record<string, string>
  timesteps?: Record<string, unknown>
}

type RunResultsBase = Record<string, Record<string, NodeResultsBase>>

/**
 * Based on run results of a node we can determine what is lag shift we should be using across the graph.
 */
export const calculateLagShift = (runResults: RunResultsBase | undefined) => {
  if (!runResults) return 0

  const firstRunNodesResults = Object.values(runResults)?.[0]
  if (!firstRunNodesResults) return 0

  const firstNodeResults = Object.entries(firstRunNodesResults)?.[0]
  if (!firstNodeResults) return 0

  const [nodeSampleLabel, nodeResults] = firstNodeResults
  if (!nodeResults?.names || !nodeResults?.timesteps) return 0

  const firstNonLagTimestep = Object.entries(nodeResults.names).find(
    ([_timestep, name]) => nodeSampleLabel === convertLabel(name),
  )?.[0]

  if (!firstNonLagTimestep) return 0

  const firstNonLagIndex = Object.keys(nodeResults.timesteps).indexOf(firstNonLagTimestep)

  return firstNonLagIndex
}

type NodeResultEntry = {
  runKey: string
  nodeName: string
  results: NodeResult[]
}

export const getNodeResultsEntries = (runsResults: RunResults | undefined): NodeResultEntry[] => {
  if (!runsResults) return []

  return Object.entries(runsResults).flatMap(([runKey, runResults]) =>
    Object.entries(runResults).map(([nodeName, nodeResults]) => ({
      runKey,
      nodeName,
      results: nodeResults,
    })),
  )
}

export const areHistoricResultsOfTheSameUnitFormat = (
  results: RunResultsHistoric | RunResultsAggregate | undefined,
) => {
  if (!results) return true

  const unitForamts = Object.values(results)
    .flatMap((runResults) => Object.values(runResults))
    .flatMap((nodeResults) => nodeResults.unitFormat)

  return new Set(unitForamts).size < 2
}

export const areResultsOfTheSameUnitFormat = (results: RunResults | undefined) => {
  if (!results) return true

  const unitForamts = Object.values(results)
    .flatMap((runResults) => Object.values(runResults))
    .flatMap((nodeResults) => nodeResults[0]?.unit_format)

  return new Set(unitForamts).size < 2
}
